import React, { useState, useEffect } from 'react';
import { projectAPI } from '../services/api';

const ProjectManagement = () => {
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientName: ''
  });
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async (includeInactive = showInactive) => {
    setLoading(true);
    try {
      const params = includeInactive ? { active: 'false' } : {};
      const response = await projectAPI.getAll(params);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      alert('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingProject) {
        await projectAPI.update(editingProject._id, formData);
        alert('Project updated successfully');
      } else {
        await projectAPI.create(formData);
        alert('Project created successfully');
      }

      setFormData({ name: '', description: '', clientName: '' });
      setEditingProject(null);
      setShowForm(false);
      fetchProjects();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to save project'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || '',
      clientName: project.clientName || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to deactivate this project?')) return;

    try {
      await projectAPI.delete(projectId);
      alert('Project deactivated successfully');
      fetchProjects();
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to delete project'}`);
    }
  };

  const getProjectHours = async (projectId) => {
    try {
      const response = await projectAPI.getHoursSummary(projectId);
      const summary = response.data;

      alert(`Project: ${summary.project.name}\n` +
            `Total Hours: ${summary.summary.totalHours}\n` +
            `Total Cost: $${summary.summary.totalCost}\n` +
            `Employees: ${summary.summary.employeeCount}`);
    } catch (error) {
      alert('Failed to fetch project hours');
    }
  };

  return (
    <div className="project-management">
      <div className="section-header">
        <h3>Project Management</h3>
        <div>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => {
              const newShowInactive = !showInactive;
              setShowInactive(newShowInactive);
              fetchProjects(newShowInactive);
            }}
            style={{ marginRight: '10px' }}
          >
            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(!showForm);
              setEditingProject(null);
              setFormData({ name: '', description: '', clientName: '' });
            }}
          >
            {showForm ? 'Cancel' : '+ Add Project'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="project-form">
          <h4>{editingProject ? 'Edit Project' : 'New Project'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Project Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Client Name</label>
                <input
                  type="text"
                  value={formData.clientName}
                  onChange={(e) => setFormData({...formData, clientName: e.target.value})}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : (editingProject ? 'Update' : 'Create')} Project
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowForm(false);
                  setEditingProject(null);
                  setFormData({ name: '', description: '', clientName: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="projects-list">
        {loading ? (
          <div className="loading">Loading projects...</div>
        ) : projects.length === 0 ? (
          <div className="empty-state">No projects found. Create your first project!</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>Client</th>
                <th>Description</th>
                <th>Status</th>
                <th>Created By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(project => (
                <tr key={project._id}>
                  <td>{project.name}</td>
                  <td>{project.clientName || '-'}</td>
                  <td>{project.description || '-'}</td>
                  <td>
                    <span className={`status ${project.isActive ? 'active' : 'inactive'}`}>
                      {project.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{project.createdBy?.firstName} {project.createdBy?.lastName}</td>
                  <td className="action-buttons">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => getProjectHours(project._id)}
                      title="View Hours"
                    >
                      Hours
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleEdit(project)}
                      title="Edit"
                    >
                      Edit
                    </button>
                    {project.isActive && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(project._id)}
                        title="Deactivate"
                      >
                        Deactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ProjectManagement;
